/**
 * Shared command-error shape for the four commands modules under
 * `src/domain` (objectives, habits, review, rewards). Every command function
 * across those modules validates a request against derived state and either
 * returns `{ events }` on success or throws this error on failure -- this
 * was previously four byte-for-byte-identical copies of the same class,
 * consolidated here since (unlike genuinely different domain math) this is
 * pure boilerplate with no domain-specific variation. `create-domain-api.ts`
 * has its own similarly-named `DomainError` class with a different
 * constructor shape (takes a full `DomainErrorInfo` object) -- that one is
 * intentionally left as its own type rather than folded in here.
 */

import type { CompassEvent, DomainErrorInfo } from '../../contracts';

export interface CommandResult<TEvent extends CompassEvent> {
  readonly events: readonly TEvent[];
}

/**
 * Real `Error` subclass (not a plain `DomainErrorInfo` object literal) so
 * throwing it preserves a stack trace and satisfies `instanceof Error`
 * checks elsewhere.
 */
export class DomainCommandError extends Error implements DomainErrorInfo {
  readonly code: DomainErrorInfo['code'];
  constructor(code: DomainErrorInfo['code'], message: string) {
    super(message);
    this.code = code;
    this.name = 'DomainCommandError';
  }
}

export function domainError(
  code: DomainErrorInfo['code'],
  message: string,
): DomainCommandError {
  return new DomainCommandError(code, message);
}
